import { useRef, useState } from 'react'
import { CircleX, TriangleAlert } from 'lucide-react'
import { API_BASE } from '../../api/client'
import { env } from '../../config/env'
import {
  backendsFor,
  clearStoredBackend,
  readStoredBackend,
  storeBackend,
  type Backend,
  type BackendId,
} from '../../config/backendOverride'
import { clearStoredTier, readStoredTier, storeTier } from '../../config/tierOverride'
import { BUILD_TIER, TIER, TIER_LABEL, type Tier } from '../../platform/access/tier'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import './developer.css'

/**
 * Settings › Developer — #449. Which API this browser talks to, and #477,
 * which entitlement tier this browser is granted.
 *
 * **Never in production.** Routed only through `SETTINGS_NAV`'s MODE-gated
 * entry and `App.tsx`'s MODE-gated `SCREENS` entry, so a production build has
 * neither the route nor this module; `backendOverride.build.test.ts` checks the
 * bundle for this page's lede. Copy lives in this file, which production never
 * emits, for the reason `backendOverride.ts` gives about data. The tier switch
 * has its own second layer: `platform/access/tier.ts`'s `TIER` refuses a
 * stored override outright on `MODE === 'production'`, so even a production
 * build that somehow shipped this page (it does not) could not act on one.
 *
 * **No server state, so no `<Async>`.** Everything shown is this build's
 * configuration and this browser's storage, both synchronous.
 *
 * **Why a switch reloads.** `api/client` resolves its base once at module
 * evaluation and the client captures it; `main.tsx`'s `EntitlementsProvider`
 * likewise reads `TIER` once. Swapping either live would also leave every
 * cached query holding the old backend's or tier's data.
 *
 * **The tier switch exists for #477's two step-8 specs**, which run against
 * app-dev — `'premium'` by default — and need to observe a LOCKED gate. They
 * drive it the same way this component does: write `'free'` to
 * `TIER_STORAGE_KEY` (`config/tierOverride.ts`) via `localStorage`, then
 * reload the page.
 *
 * **Sign-in does not follow the backend.** The build's Firebase project stays;
 * today all three backends verify tokens from that same project, which is why a
 * switch keeps you signed in. The last paragraph on the page says so, because
 * it stops being true the day backend tiers get their own projects (#177) —
 * unrelated to the entitlement tier this page also switches.
 *
 * **Two regions, not one section — #482.** #477 added the tier block's own
 * `<h2>` inside the SAME landmark the Backend block already used, so a query
 * had nothing to scope to and both blocks' `In use:` paragraphs collided in
 * `e2e/specs/step8-dev-developer-settings.spec.ts`. Each block is now its own
 * `<section aria-labelledby>`, matching `SettingsLayout`'s nav heading
 * pattern: a real `<h2>` element referenced by id, not a duplicated
 * `aria-label`. `getByRole('region', { name: 'Backend' })` and
 * `{ name: 'Entitlement tier' }` each resolve to exactly one element now.
 */

type Props = {
  /** Injected so a test can stand on another site; the page reads the real one. */
  origin?: string
  reload?: () => void
}

const STORAGE_REFUSED =
  "This browser couldn't save the choice (storage is blocked), so nothing changed."

/** `https://dev-api.tracke5.com` → `dev-api.tracke5.com`, the part a person recognises. */
function hostOf(base: string): string {
  try {
    return new URL(base).host
  } catch {
    return base
  }
}

export function Developer({
  origin = window.location.origin,
  reload = () => window.location.reload(),
}: Props) {
  const buildDefault = env.apiBase
  const offered = backendsFor(origin)
  const [stored, setStored] = useState(readStoredBackend)
  const [confirming, setConfirming] = useState<Backend | null>(null)
  const [refused, setRefused] = useState(false)
  // The Use button that opened the confirm step, for focus to return to.
  const opener = useRef<HTMLButtonElement | null>(null)

  const inUse = hostOf(API_BASE)
  const overridden = API_BASE !== buildDefault
  const ignored = stored !== null && !offered.some((b) => b.id === stored)

  const [storedTier, setStoredTier] = useState(readStoredTier)
  const [tierRefused, setTierRefused] = useState(false)
  const otherTier: Tier = TIER === 'premium' ? 'free' : 'premium'
  const tierOverridden = TIER !== BUILD_TIER

  function applyTier(tier: Tier) {
    setTierRefused(false)
    if (!storeTier(tier)) {
      setTierRefused(true)
      return
    }
    reload()
  }

  function resetTier() {
    setTierRefused(false)
    if (!clearStoredTier()) {
      setTierRefused(true)
      return
    }
    setStoredTier(null)
    reload()
  }

  function apply(id: BackendId) {
    if (!storeBackend(id)) {
      setRefused(true)
      return
    }
    reload()
  }

  function choose(backend: Backend, button: HTMLButtonElement) {
    setRefused(false)
    if (backend.warning !== null) {
      opener.current = button
      setConfirming(backend)
      return
    }
    apply(backend.id)
  }

  function reset() {
    setRefused(false)
    if (!clearStoredBackend()) {
      setRefused(true)
      return
    }
    setStored(null)
    reload()
  }

  return (
    <div className="developer">
      <section className="developer__section" aria-labelledby="developer-backend-heading">
        <h2 id="developer-backend-heading" className="developer__title">
          Backend
        </h2>
        <p className="developer__lede">
          Choose which API this browser talks to. Switching reloads the page.
        </p>

        <p className="developer__current">
          In use: <strong className="developer__host">{inUse}</strong>{' '}
          {overridden ? "(your choice, not this build's default)" : "(this build's default)"}
        </p>

        {ignored && (
          <p className="developer__note">
            Your saved choice isn&apos;t reachable from this site, so this build&apos;s default
            applies instead.
          </p>
        )}

        {offered.length === 0 ? (
          <p className="developer__note">
            No backend accepts requests from {origin}, so this build&apos;s default is the only
            one this site can use.
          </p>
        ) : (
          <ul className="developer__list">
            {offered.map((backend) => {
              const current = backend.base === API_BASE
              return (
                <li className="developer__row" key={backend.id}>
                  <div className="developer__backend">
                    <span className="developer__name">
                      {backend.label}
                      {backend.base === buildDefault && (
                        <span className="developer__tag">Build default</span>
                      )}
                    </span>
                    <span className="developer__row-host">{hostOf(backend.base)}</span>
                    {backend.warning !== null && (
                      <span className="developer__warning">
                        <Icon glyph={TriangleAlert} />
                        {backend.warning}
                      </span>
                    )}
                  </div>
                  {current ? (
                    <span className="developer__in-use">In use</span>
                  ) : (
                    <Button
                      variant="outline"
                      size="page"
                      onClick={(e) => choose(backend, e.currentTarget)}
                    >
                      Use {backend.label}
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {refused && (
          <p className="developer__error" role="alert">
            <span className="developer__error-mark">
              <Icon glyph={CircleX} />
            </span>
            {STORAGE_REFUSED}
          </p>
        )}

        {stored !== null && (
          <div className="developer__actions">
            <Button variant="outline" size="page" onClick={reset}>
              Reset to this build&apos;s default
            </Button>
          </div>
        )}

        <p className="developer__note">
          Sign-in stays with this build&apos;s Firebase project. Dev and Staging both accept its
          tokens today. The local stack accepts them when its backend has the Firebase
          service-account key at <code>~/.config/tracke5/e2e-firebase-sa.json</code>; without it,
          every signed-in request gets a 401.
        </p>
      </section>

      <section className="developer__section" aria-labelledby="developer-tier-heading">
        <h2 id="developer-tier-heading" className="developer__title">
          Entitlement tier
        </h2>
        <p className="developer__lede">
          Override which entitlement tier this build grants. Switching reloads the page.
          Production never honours this choice.
        </p>

        <p className="developer__current">
          In use: <strong className="developer__in-use">{TIER_LABEL[TIER]}</strong>{' '}
          {tierOverridden ? "(your choice, not this build's default)" : "(this build's default)"}
        </p>

        {/* Hidden while an override is active: Tier is binary, so the flip
            side of any active override IS the build's own default, and "Use
            <build tier>" would then do the same thing as Reset below — store
            the build tier explicitly vs clear the stored value, two
            mechanisms landing on the same TIER. Only Reset is offered then. */}
        {!tierOverridden && (
          <div className="developer__actions">
            <Button variant="outline" size="page" onClick={() => applyTier(otherTier)}>
              Use {TIER_LABEL[otherTier]}
            </Button>
          </div>
        )}

        {tierRefused && (
          <p className="developer__error" role="alert">
            <span className="developer__error-mark">
              <Icon glyph={CircleX} />
            </span>
            {STORAGE_REFUSED}
          </p>
        )}

        {storedTier !== null && (
          <div className="developer__actions">
            <Button variant="outline" size="page" onClick={resetTier}>
              Reset entitlement tier to this build&apos;s default
            </Button>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={confirming !== null}
        title={`Switch to ${confirming?.label ?? ''}?`}
        body={`${confirming?.warning ?? ''} The page reloads.`}
        confirmLabel={`Switch to ${confirming?.label ?? ''}`}
        destructive
        mode="switch_backend"
        restoreFocusTo={opener}
        onCancel={() => setConfirming(null)}
        onConfirm={async () => {
          if (confirming === null) return
          if (!storeBackend(confirming.id)) throw new Error('storage refused')
          reload()
        }}
        errorFor={() => STORAGE_REFUSED}
      />
    </div>
  )
}
